import { type FormInstance } from 'antd';
import { jwtDecode } from 'jwt-decode';
import type { StoreApi } from 'zustand';

import type { IUserRoleEnum } from '~/enums/userRole.enum';
import { fetchLogin } from '~/services/auth/AuthService';
import { useTokenStore } from '~/stores/token/useTokenStore';
import type { ILoginParams } from '~/types/api/Auth';
import { toClaimArray } from '~/utils/auth/normalizeClaim';
import type { IAuthStore, IVeilingTokemClaim } from './useAuthStore';

export type ILoginFormFilter = {
  username: string;
  password: string;
  rememberMe: boolean;
};

export type ILoginActions = {
  resetStates: () => void;
  setFormFilterInstance: (form: Partial<FormInstance<ILoginFormFilter>>) => void;
  getParams: () => ILoginParams;
  handleSubmit: () => Promise<void>;
};

export type ILoginState = {
  isLoading: boolean;
  formFilterInstance: Partial<FormInstance<ILoginFormFilter>> | null;
};

export type ILoginSlice = ILoginState & ILoginActions;

const useLoginSlice = (
  set: StoreApi<IAuthStore>['setState'],
  get: StoreApi<IAuthStore>['getState'],
): ILoginSlice => ({
  // States
  isLoading: false,
  formFilterInstance: null,

  // Setters
  setFormFilterInstance: form => {
    get().setData(state => {
      state.loginSlice.formFilterInstance = form;
    });
  },

  // Actions
  resetStates: () => {
    get().setData(state => {
      state.loginSlice.isLoading = false;
      state.loginSlice.formFilterInstance = null;
    });
  },
  getParams: () => {
    const { formFilterInstance } = get().loginSlice;
    if (!formFilterInstance?.getFieldsValue) {
      throw new Error('Instância do formulário não está definida.');
    }

    const formValues = formFilterInstance.getFieldsValue();

    return {
      username: formValues.username.toLocaleLowerCase(),
      password: formValues.password,
      rememberMe: formValues.rememberMe ?? false,
    } as ILoginParams;
  },
  handleSubmit: async () => {
    const { formFilterInstance } = get().loginSlice;
    if (!formFilterInstance) return;

    get().setData(state => {
      state.loginSlice.isLoading = true;
    });

    try {
      const params = get().loginSlice.getParams();
      const response = await fetchLogin(params);
      const dataResponse = response.data;

      useTokenStore.setAll({
        accessToken: dataResponse.access_token,
        refreshToken: dataResponse.refresh_token,
        expiresIn: String(dataResponse.expires_in),
        scope: dataResponse.scope,
        tokenType: dataResponse.token_type,
      });

      const decodedToken = jwtDecode(dataResponse.access_token) as IVeilingTokemClaim;

      get().setData(state => {
        state.tokenClaim.roleId = toClaimArray(decodedToken.veiling_claim_type_role).map(
          Number,
        ) as IUserRoleEnum[];
        state.tokenClaim.identifier = Number(decodedToken.veiling_claim_type_identifier);
        state.tokenClaim.email = decodedToken.veiling_claim_type_email;
        state.tokenClaim.name = decodedToken.veiling_claim_type_name;
        state.tokenClaim.allowedSystems = toClaimArray(
          decodedToken.veiling_claim_type_allowed_systems,
        );
        state.loginSlice.isLoading = false;
      });

      return Promise.resolve();
    } catch (error) {
      get().setData(state => {
        state.loginSlice.isLoading = false;
      });
      return Promise.reject(error);
    }
  },
});

export default useLoginSlice;
